import type { LogActivityFieldValue } from 'ts-activities/eval';
import type { ActivityLayoutItem } from 'ts-activities/types';
import {
  type Dispatch,
  type ReducerAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useAsync } from 'react-use';
import { isEmailField } from 'checks/fields';
import { ASSIGNMENT_SET_MULTIPLE } from 'components/Charts/ScheduledActivities/assignmentHelpers';
import { validateEmail } from 'components/Inputs/TextInput/presets/EmailAddress';
import { useToast, toastVariant } from '__components/ToastProvider';
import { useCamelCaseActivityQuery } from '__queries/models/activites';
import ActivityService from 'services/ActivityService';
import { getOriginalError } from 'services/AxiosService';
import { camelToSnakeCaseKeys } from '__services/helpers';
import { useActivityRules } from 'ts-activities/useActivityRules';
import { FORMDATA_FIELDS, EMPTY_ARRAY, FORM_ACTIONS } from './config';
import {
  useActivityAssociation,
  getFieldValueFromAPI,
  isEmptyValue,
  mapFields,
  setInitialValue,
} from './helpers';

type Field = {
  id: string;
  name: string;
  displayName: string;
  customObjectField: null | {
    id: string;
    name: string;
    displayName: string;
    fieldType: string;
    isDefault: boolean;
    customObject: {
      id: string;
      name: string;
      objectType: string;
      objectName: string;
      entityName: string;
      fetchUrl: string;
      associationSource: string;
    };
  };
  isRequired: boolean;
  isReadOnly: boolean;
  isHidden: boolean;
  isDeletable: boolean;
  isHideable: boolean;
  order: number;
  meta: { cols?: number; isReadonly?: boolean };
  properties: {
    allowOptions: boolean;
    allowMultiple: boolean;
    allowRelations: boolean;
    allowAdd: boolean;
    type: string;
    targetColumn: string;
  };
  relation: any;
  rating: any;
  phonenumberOptions: any;
  decimalOptions: any;
  fieldType: string;
  options: {
    id: string;
    code: string;
    name: string;
    order: number;
    meta: Record<string, any>;
  }[];
  access: { edit: boolean };
};

export type FormData = {
  fields: {
    customObject?: string;
    customObjectFieldId?: string;
    displayName: string;
    fieldData: Field;
    fieldType: LogActivityFieldValue['fieldType'];
    id: string;
    name: string;
    value: any;
    valuePlaceholder: any;
  }[];
  mentions: any[];
  notes: string;
  relatedObjects: {
    customObject: string;
    entityId: string;
    isContact: boolean;
  }[];
  scheduledActivityId?: string;
};

type EntityFieldValue = {
  display_name: string;
  fieldType: string;
  id: string;
  value: any;
};

export type CompleteActivityWizardContext = {
  activity: any;
  activityFields: Field[];
  /**
   * `predefinedOptions` come from scheduled activity configuratin or when
   * logging an activity on a record detail page. An association readonly
   * configuration only applies for the former - you can always change an
   * association that we pre-load from the current record.
   */
  areDefaultAssignmentsFromScheduledActivity: boolean;
  assignments: Record<string, any>;
  errors: boolean[];
  formData: FormData;
  isActivityDisabled: boolean;
  isDirty: boolean;
  isLoadingActivityAssociations: boolean;
  isLoading: boolean;
  /**
   * Some shared components between the scheulde and complete modals were refactored
   * to consume this context directly. This is set to `true` in the default context so
   * the component knows to disregard this context for the scheduled activity modal.
   */
  isScheduledActivity: boolean;
  loadActivityError: boolean;
  predefinedOptions?: CompleteActivityWizardContextProps['predefinedOptions'];
  requiredAssociations: string[];
  /**
   * @param id - a field's id or text block's internal_name
   */
  isRuleActiveForLayoutItem(id: string): boolean;
  resetField(): void;
  shouldDisableField(field: Field): boolean;
  updateField(
    field: any,
    value: any | ((v: any[]) => any[]),
    shouldIgnoreDirty?: boolean
  ): void;
  activityRelatedObjects: any;
  dispatchAssignment: Dispatch<ReducerAction<any>>;
};

type CompleteActivityWizardContextProps = {
  areDefaultAssignmentsFromScheduledActivity: boolean;
  children: any;
  data: {
    id?: string;
    activityObject: Record<string, any>;
    associatedEntities: any[];
    mentions?: any[];
    note?: string;
  };
  isLoadingPredefinedOptions: boolean;
  predefinedOptions?: {
    clientOption: { value: string; label: string };
    companyOption: {
      id?: string;
      display_name?: string;
      name?: string;
    };
    defaultAssignment: Record<string, { value: string; label: string }> & {
      objectId: string;
    };
  };
};

type UseActivityConfigurationResult = Pick<
  CompleteActivityWizardContext,
  'isDirty' | 'formData' | 'resetField' | 'updateField'
>;

const context = createContext<CompleteActivityWizardContext>({
  activity: null,
  activityFields: EMPTY_ARRAY,
  areDefaultAssignmentsFromScheduledActivity: false,
  assignments: {},
  activityRelatedObjects: [],
  errors: [],
  formData: {
    fields: EMPTY_ARRAY,
    mentions: EMPTY_ARRAY,
    notes: '',
    relatedObjects: EMPTY_ARRAY,
  },
  isActivityDisabled: false,
  isDirty: false,
  isLoading: false,
  isLoadingActivityAssociations: false,
  isScheduledActivity: true,
  predefinedOptions: undefined,
  loadActivityError: false,
  requiredAssociations: [],
  dispatchAssignment: () => null,
  isRuleActiveForLayoutItem: () => true,
  resetField: () => null,
  shouldDisableField: () => false,
  updateField: () => null,
});

export const useCompleteActivityWizardContext = () => {
  return useContext(context);
};

const { SCHEDULED_ACTIVITY_ID, NOTES, FIELDS, RELATED_OBJECTS, MENTIONS } =
  FORMDATA_FIELDS;

const activityInit = (scheduledActivity: any) => ({
  [SCHEDULED_ACTIVITY_ID]: scheduledActivity.id,
  [NOTES]: scheduledActivity.note || '',
  [FIELDS]: EMPTY_ARRAY,
  [RELATED_OBJECTS]: EMPTY_ARRAY,
  [MENTIONS]: scheduledActivity.mentions
    ? scheduledActivity.mentions.map(({ id }: any) => id)
    : EMPTY_ARRAY,
});

const activityReducer = (state: any, { type, payload }: any) => {
  switch (type) {
    case FORM_ACTIONS.update: {
      const { field, value } = payload;
      return {
        ...state,
        [field]: typeof value === 'function' ? value(state[field]) : value,
      };
    }
    case FORM_ACTIONS.reset: {
      const { scheduledActivity } = payload;
      return activityInit(scheduledActivity);
    }
    default:
      throw new Error('Unknown action');
  }
};

function useActivityConfiguration(
  scheduledActivity: any = {}
): UseActivityConfigurationResult {
  const [formData, dispatchFormData] = useReducer(
    activityReducer,
    scheduledActivity,
    activityInit
  );
  const [isDirty, setIsDirty] = useState(false);

  const updateField = useCallback(
    (f: any, value: any, shouldIgnoreDirty: boolean) => {
      if (!shouldIgnoreDirty) {
        setIsDirty(true);
      }

      dispatchFormData({
        type: FORM_ACTIONS.update,
        payload: { field: f, value },
      });
    },
    [dispatchFormData]
  );

  const resetField = useCallback(() => {
    setIsDirty(false);
    dispatchFormData({
      type: FORM_ACTIONS.reset,
      payload: { scheduledActivity },
    });
  }, [scheduledActivity, dispatchFormData]);

  return {
    formData,
    updateField,
    resetField,
    isDirty,
  };
}

export const CompleteActivityWizardContext = (
  props: CompleteActivityWizardContextProps
) => {
  const {
    children,
    data,
    isLoadingPredefinedOptions,
    predefinedOptions,
    areDefaultAssignmentsFromScheduledActivity,
  } = props;
  const activityId = data?.activityObject?.id;

  const { t } = useTranslation();
  const [showToast] = useToast();

  const {
    data: activity,
    error,
    isError,
    isLoading: loadingActivity,
    isStale,
  } = useCamelCaseActivityQuery(activityId, {
    retry: false,
  });

  const isActivityDisabled = activity?.enabled === false;
  const activityName = activity?.name ?? '';

  useEffect(() => {
    if (!isStale && isActivityDisabled) {
      showToast({
        message: t(
          "Activity '{{activityName}}' is disabled and cannot be completed.",
          { activityName }
        ),
        variant: toastVariant.FAILURE,
      });
    }
  }, [isActivityDisabled, activityName, isStale, showToast, t]);

  const { formData, updateField, resetField, isDirty } =
    useActivityConfiguration(data);

  const entityValuesRef = useRef<
    Record<string, Record<string, EntityFieldValue['value']>>
  >({});
  const fetchedEntitiesRef = useRef<Record<string, boolean>>({});

  const { fields, relatedObjects } = formData;

  const shouldDisableField = useCallback(
    (field: Field) => {
      const readonly = Boolean(field.meta.isReadonly);

      const hasInitialValue = relatedObjects.some((obj) => {
        return entityValuesRef.current[obj.entityId]?.[field.id];
      });

      if (!hasInitialValue && field.isRequired && readonly) {
        return false;
      }

      return readonly;
    },
    [relatedObjects]
  );

  const { isRuleActiveForLayoutItem } = useActivityRules(
    camelToSnakeCaseKeys(activity?.visibilityRules),
    fields
  );

  const {
    assignments,
    dispatchAssignment,
    activityRelatedObjects,
    isFetching: isLoadingActivityAssociations,
  } = useActivityAssociation(activityId, predefinedOptions, {
    retry: false,
  });

  const { value: activityFields = [], loading: loadingActivityFields } =
    useAsync(async (): Promise<Field[]> => {
      const rawFields = activityId
        ? await ActivityService.getObjectFields({
            activityObjectId: activityId,
          })
        : [];
      return rawFields.map(mapFields);
    }, [activityId]);

  useEffect(() => {
    if (isError) {
      const err = ActivityService.notFoundError(error);

      const orig = getOriginalError(err);
      showToast({
        message:
          orig?.message ||
          t(
            'There was an error loading the {{activityName}}. Please fix and try again.',
            {
              activityName: t('Chosen Activity'),
            }
          ),
        variant: toastVariant.FAILURE,
      });
    }
  }, [isError, error, showToast, t]);

  useEffect(() => {
    if (data?.associatedEntities?.length && activityRelatedObjects.length) {
      const value = data.associatedEntities.reduce((acc, entity) => {
        const relObject = activityRelatedObjects.find(
          (act) => act.id === entity.customObjectId
        );
        if (relObject) {
          const { name } = relObject.component.setterProp;
          const value = {
            label: entity.displayName || entity.name,
            value: entity.entityId,
          };
          acc[name] = value;
        }
        return acc;
      }, {});
      dispatchAssignment({
        type: ASSIGNMENT_SET_MULTIPLE,
        value,
      });
    }
  }, [data, activityRelatedObjects, dispatchAssignment]);

  const setInitialValues = useCallback(() => {
    updateField(
      FIELDS,
      (prevFields: FormData['fields']) => {
        return activityFields.map((activityField) => {
          const { displayName, fieldType, id, customObjectField, name } =
            activityField;
          const prevValue = prevFields.find((field) => field?.id === id)?.value;
          return {
            id,
            displayName,
            name,
            fieldType,
            customObjectFieldId: customObjectField?.id,
            customObject: customObjectField?.customObject?.id,
            value: isEmptyValue(prevValue)
              ? setInitialValue({ fieldType })
              : prevValue,
            fieldData: activityField,
          };
        });
      },
      true
    );
  }, [activityFields, updateField]);

  useEffect(() => {
    setInitialValues();
  }, [setInitialValues]);

  const setValues = useCallback(
    async (objectId: string, recordId: string) => {
      let fieldsValues: EntityFieldValue[] = [];

      if (objectId && recordId) {
        fieldsValues = await ActivityService.getEntityRecordsFieldValues(
          activityId,
          recordId
        );
        entityValuesRef.current[recordId] = {};
      }

      updateField(
        FIELDS,
        (prev: FormData['fields']) => {
          return prev.map((prevField) => {
            const shouldFieldBeUpdated = prevField.customObject === objectId;
            const fieldFromAPI =
              shouldFieldBeUpdated &&
              fieldsValues.find((f) => f.id === prevField.customObjectFieldId);

            if (fieldFromAPI) {
              entityValuesRef.current[recordId][prevField.id] =
                fieldFromAPI.value;
            }

            if (shouldFieldBeUpdated && !fieldFromAPI) {
              return {
                ...prevField,
                value: setInitialValue(
                  activityFields.find(({ id }) => id === prevField.id)
                ),
              };
            }
            return fieldFromAPI
              ? {
                  ...fieldFromAPI,
                  id: prevField.id,
                  customObject: objectId,
                  customObjectFieldId: prevField.customObjectFieldId,
                  value: isEmptyValue(fieldFromAPI.value)
                    ? setInitialValue(
                        activityFields.find(({ id }) => id === prevField.id)
                      )
                    : getFieldValueFromAPI(fieldFromAPI),
                  fieldData: prevField.fieldData,
                }
              : prevField;
          });
        },
        true
      );
    },
    [activityId, updateField, activityFields]
  );

  // this will run whenever we change any association, it will map through all associations to decide to fetch selected entitites' field values
  // and here are the rules:
  // 1. if we did not have this entitiy before the change, we fetch it's values and replace previuosly set (or empty) fields with the fetched
  // values.
  // 2. if we had this entity before the change, we do not fetch it's values again and leave fields in previous state.
  // 3. if the association was cleared (entity removed) we leave fields in previous state.
  const loadRelatedObjectFieldValues =
    !!activityFields.length && !!relatedObjects.length && !!activityId;

  useEffect(() => {
    const load = async () => {
      const lookup = relatedObjects.reduce<Record<string, boolean>>(
        (acc, { entityId }) => {
          acc[entityId] = true;
          return acc;
        },
        {}
      );

      // filter out fetched entities that are not in the current related objects
      fetchedEntitiesRef.current = Object.keys(
        fetchedEntitiesRef.current
      ).reduce<Record<string, boolean>>((acc, key) => {
        if (lookup[key]) {
          acc[key] = fetchedEntitiesRef.current[key];
        }
        return acc;
      }, {});

      const promises: Promise<void>[] = [];

      // map through related objects and fetch field values for newly added
      relatedObjects.forEach(({ customObject, entityId }) => {
        if (!fetchedEntitiesRef.current[entityId]) {
          promises.push(setValues(customObject, entityId));
          fetchedEntitiesRef.current[entityId] = !!entityId;
        }
      });

      await Promise.all(promises);
    };

    if (loadRelatedObjectFieldValues) {
      load();
    } else {
      setInitialValues();
    }
  }, [
    loadRelatedObjectFieldValues,
    relatedObjects,
    setValues,
    setInitialValues,
  ]);

  const isLoading =
    loadingActivity ||
    loadingActivityFields ||
    isLoadingActivityAssociations ||
    isLoadingPredefinedOptions;

  const fieldsById = fields.reduce<Record<string, FormData['fields'][number]>>(
    (acc, field) => {
      acc[field.id] = field;
      return acc;
    },
    {}
  );

  const activityFieldsById = useMemo(() => {
    return activityFields.reduce<Record<string, Field>>((acc, field) => {
      acc[field.id] = field;
      return acc;
    }, {});
  }, [activityFields]);

  const requiredAssociations = useMemo(() => {
    if (!activity?.associatedObjects) {
      return [];
    }

    return activity.associatedObjects.reduce((acc: string[], ao: any) => {
      if (ao.isRequired) {
        acc.push(ao.customObject.id);
      }

      return acc;
    }, []);
  }, [activity]);

  let errors: boolean[] = [];

  if (!isLoading && fields.length) {
    errors = activity.layout.map((item: ActivityLayoutItem) => {
      if (item.type === 'section') {
        return false;
      }

      const field = fieldsById[item.fieldId];
      const activityField = activityFieldsById[item.fieldId];

      if (!activityField || !field) {
        return false;
      }

      if (isRuleActiveForLayoutItem(item.fieldId)) {
        if (isEmailField(field)) {
          return (
            activityField.isRequired && validateEmail(field.value) === null
          );
        } else {
          return activityField.isRequired && isEmptyValue(field.value);
        }
      }
      return false;
    });
  }

  const ctx = {
    activity,
    activityFields,
    activityRelatedObjects,
    areDefaultAssignmentsFromScheduledActivity,
    assignments,
    errors,
    formData,
    isDirty,
    isLoadingActivityAssociations,
    isLoading,
    isActivityDisabled,
    isScheduledActivity: false,
    loadActivityError: isError,
    predefinedOptions,
    requiredAssociations,
    dispatchAssignment,
    isRuleActiveForLayoutItem,
    resetField,
    shouldDisableField,
    updateField,
  };

  return <context.Provider value={ctx}>{children}</context.Provider>;
};
